import * as React from 'react'
import { Download, FileText, Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'

type Gift = {
  id: number
  gift_on: string
  campaign: string
  method: string
  amount: string
  refunded: boolean
  receipt_available: boolean
}

type YearEndStatement = {
  year: number
  url: string
}

type ActivePlan = {
  amount: string
  frequency: 'monthly' | 'annual'
  status: 'active' | 'paused'
  next_charge_on: string
  paused_until?: string | null
  card: { brand: string; last4: string }
  campaign: string
  manage_url: string
}

export function MemberPortalGiving() {
  const [search, setSearch] = React.useState('')
  const [sort, setSort] = React.useState<'newest' | 'oldest'>('newest')
  const query = new URLSearchParams({ ...(search ? { search } : {}), sort }).toString()
  const giving = useQuery<{
    giving: {
      active_plan: ActivePlan | null
      gifts: Gift[]
      year_end_statements: YearEndStatement[]
    }
  }>({
    queryKey: ['member-portal-giving', query],
    queryFn: () => getJSON(`/api/v1/member_portal/giving?${query}`),
    retry: false,
  })
  if (giving.isLoading)
    return (
      <p className="mt-4 text-body-sm text-muted-foreground" role="status">
        Loading Giving...
      </p>
    )
  if (giving.isError || !giving.data)
    return <Alert className="mt-4">We couldn’t load your Giving history.</Alert>
  const gifts = giving.data.giving.gifts
  const statements = giving.data.giving.year_end_statements
  const activePlan = giving.data.giving.active_plan
  return (
    <section className="mt-4 space-y-3">
      {activePlan ? <ActivePlanCard plan={activePlan} /> : <NoPlan />}
      <div className="relative w-full max-w-xs">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search gifts"
          className="h-9 pl-9"
        />
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        <Table className="min-w-[680px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableTableHead
                active
                direction={sort === 'oldest' ? 'ascending' : 'descending'}
                onToggle={() => setSort(sort === 'newest' ? 'oldest' : 'newest')}
              >
                Date
              </SortableTableHead>
              <TableHead>Campaign</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gifts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No Giving history yet.
                </TableCell>
              </TableRow>
            ) : (
              gifts.map((gift) => (
                <TableRow key={gift.id} className={gift.refunded ? 'text-muted-foreground' : ''}>
                  <TableCell>{displayDate(gift.gift_on)}</TableCell>
                  <TableCell>{gift.campaign}</TableCell>
                  <TableCell>{gift.method}</TableCell>
                  <TableCell>
                    {gift.receipt_available && (
                      <Button
                        asChild
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="size-8 px-0 shadow-none"
                        aria-label="View receipt"
                      >
                        <a href={`/api/v1/member_portal/giving/${gift.id}/receipt`}>
                          <FileText className="size-3.5" aria-hidden="true" />
                        </a>
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {gift.amount}
                    {gift.refunded && (
                      <span className="mt-0.5 block text-caption font-normal">Refunded</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {statements.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
          <p className="mr-auto text-caption text-muted-foreground">
            Mailed every January for the prior year.
          </p>
          {statements.map((statement) => (
            <Button key={statement.year} asChild variant="outline" size="sm" className="h-8 gap-2">
              <a href={statement.url}>
                <Download className="size-3.5" aria-hidden="true" />
                {statement.year} giving statement
              </a>
            </Button>
          ))}
        </div>
      )}
    </section>
  )
}

function ActivePlanCard({ plan }: { plan: ActivePlan }) {
  const frequency = plan.frequency === 'monthly' ? 'Monthly' : 'Annual'
  return (
    <article className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-body-sm font-medium text-foreground">{frequency} gift</h2>
        <Badge variant={plan.status === 'paused' ? 'warning' : 'success'}>
          {plan.status === 'paused' ? 'Paused' : 'Active'}
        </Badge>
      </div>
      <p className="mt-3 text-body-sm text-foreground">
        {plan.amount} · {frequency} ·{' '}
        {plan.status === 'paused'
          ? `resumes ${displayDate(plan.paused_until ?? '')}`
          : `next charge ${displayDate(plan.next_charge_on)}`}
      </p>
      <p className="mt-0.5 text-caption text-muted-foreground">
        {plan.card.brand} ·· {plan.card.last4} · {plan.campaign}
      </p>
      <div className="mt-4 border-t border-border pt-3">
        <Button asChild variant="outline" size="sm" className="h-8">
          <Link to={plan.manage_url}>Manage</Link>
        </Button>
      </div>
    </article>
  )
}

function NoPlan() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed border-border px-4 py-3">
      <p className="text-caption text-muted-foreground">No recurring gift.</p>
      <Button asChild variant="outline" size="sm" className="h-8">
        <Link to="/give">Start a monthly gift</Link>
      </Button>
    </div>
  )
}
